import { NavLink, Link } from "react-router-dom";
import { apiRequest, formatCurrency, getImageUrl } from "../../core/common/api";
import AppImage from "../../shared/AppImage";
import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import Swal from "sweetalert2";
import MetricCard from "../../shared/MetricCard";

const Dashboard = () => {
  const [data, setData] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);

  const fetchDashboard = async () => {
    setIsLoading(true);

    try {
      const result = await apiRequest("/dashboard", "GET");
      if (result.success) {
        setData(result);
      } else {
        Swal.fire({
          title: result.message || "Unable to load dashboard",
          icon: "error",
        });
      }
    } catch (error: any) {
      Swal.fire({
        title: error.message || "Unable to load dashboard",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const stats = data.stats || {};

  const metrics = [
    {
      label: "Customers",
      value: stats.usersCount ?? 0,
      helper: "registered users",
      icon: "🛒",
      tone: "orange",
    },
    {
      label: "Orders",
      value: stats.ordersCount ?? 0,
      helper: "total orders",
      icon: "📦",
      tone: "blue",
    },

    {
      label: "Total Products",
      value: stats.productsCount ?? 0,
      helper: "total listings",
      icon: "🛍️",
      tone: "purple",
    },
    {
      label: "Total Categories",
      value: stats.categoriesCount ?? 0,
      helper: "active categories",
      icon: "🗂️",
      tone: "pink",
    },
    {
      label: "Revenue",
      value: formatCurrency(stats.totalRevenue),
      helper: "open order value",
      icon: "💰",
      tone: "green",
    },
  ];

  const orderSources = (data.orderStatusSources || []).map(
    (source: any, index: number) => ({
      ...source,
      color: ["purple", "blue", "green", "orange", "pink"][index % 5],
    }),
  );

  const mapProductRows = (products: any[] = []) =>
    products.map((product: any) => ({
      id: product._id,
      name: product.name,
      sold: `${product.sold || 0} sold`,
      amount: formatCurrency(product.sellingPrice || product.price || 0),
      stock: `${product.stock ?? 0} in stock`,
      image: getImageUrl(product.images?.[0]),
    }));

  const bestSellerRows = mapProductRows(data.bestSellers);
  const featuredRows = mapProductRows(data.featuredProducts);
  const newArrivalRows = mapProductRows(data.newArrivals);

  const activityRows = (data.activity || [])
    .slice(0, 5)
    .map((item: any) => ({
      id: item.id,
      title: item.title,
      meta: item.meta,
      time: item.time,
      status: item.status,
    }));

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">Overview</div>
          <h1 className="page-title">
            Admin <em>Dashboard</em>
          </h1>
          <div className="page-sub">
            Track customers, products, orders and recent activity.
          </div>
        </div>
        <div className="page-hdr-actions">
          <button
            className="btn-sm btn-outline"
            onClick={fetchDashboard}
            disabled={isLoading}
          >
            <RefreshCw size={16} aria-hidden="true" />
            Refresh
          </button>
        </div>
      </div>
      <section className={`metrics-grid ${isLoading ? "loading-dim" : ""}`}>
        {metrics.map((metric) => (
          <MetricCard key={metric.label} metric={metric} />
        ))}
      </section>

      <section className="dashboard-grid">
        <div className="card dashboard-status-card">
          <div className="card-header">
            <div>
              <h2>Orders by Status</h2>
            </div>
            <NavLink to="/orders">View orders</NavLink>
          </div>
          {orderSources.length === 0 ? (
            <div className="dashboard-empty">No orders yet.</div>
          ) : (
            <div className="card-body order-status-content">
              <div className="order-status-chart-wrap">
                <div
                  className="donut-chart"
                  style={{ background: buildConicGradient(orderSources) }}
                >
                  <strong>{stats.ordersCount || 0}</strong>
                  <span>Total Orders</span>
                </div>
              </div>
              <div className="order-status-list">
                {orderSources.map((source: any) => (
                  <div key={source.label} className="order-status-row">
                    <div className="order-status-row-head">
                      <p>
                        <span className={`order-status-dot ${source.color}`} />
                        {source.label}
                      </p>
                      <strong>
                        {source.value}
                        <small>{Number(source.share || 0).toFixed(1)}%</small>
                      </strong>
                    </div>
                    <div className="order-status-track">
                      <span
                        className={source.color}
                        style={{
                          width: `${Math.min(
                            Math.max(Number(source.share || 0), 0),
                            100,
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <h2>Best Sellers</h2>
            <NavLink to="/products?tag=bestSeller">View all</NavLink>
          </div>
          <div className="card-body product-list">
            {bestSellerRows.length === 0 ? (
              <div className="empty-state">No best sellers marked.</div>
            ) : (
              bestSellerRows.map((product: any) => (
                <div key={product.id} className="product-row">
                  <AppImage src={product.image} alt={product.name} crossOrigin="anonymous" />
                  <div>
                    <strong>{product.name}</strong>
                    <small>{product.sold}</small>
                  </div>
                  <b>{product.amount}</b>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2>Featured Products</h2>
            <NavLink to="/products?tag=featured">View all</NavLink>
          </div>
          <div className="card-body product-list">
            {featuredRows.length === 0 ? (
              <div className="empty-state">No featured products marked.</div>
            ) : (
              featuredRows.map((product: any) => (
                <div key={product.id} className="product-row">
                  <AppImage src={product.image} alt={product.name} crossOrigin="anonymous" />
                  <div>
                    <strong>{product.name}</strong>
                    <small>{product.stock}</small>
                  </div>
                  <b>{product.amount}</b>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2>New Arrivals</h2>
            <NavLink to="/products?tag=newArrival">View all</NavLink>
          </div>
          <div className="card-body product-list">
            {newArrivalRows.length === 0 ? (
              <div className="empty-state">No new arrivals marked.</div>
            ) : (
              newArrivalRows.map((product: any) => (
                <div key={product.id} className="product-row">
                  <AppImage src={product.image} alt={product.name} crossOrigin="anonymous" />
                  <div>
                    <strong>{product.name}</strong>
                    <small>{product.stock}</small>
                  </div>
                  <b>{product.amount}</b>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="card activity-card">
          <div className="card-header">
            <h2>Order Activity</h2>
            <NavLink to="/orders">View all</NavLink>
          </div>
          <div className="activity-list">
            {activityRows.length === 0 ? (
              <div className="empty-state">No activity found.</div>
            ) : (
              activityRows.map((item: any) => (
                <Link
                  key={item.id}
                  to={`/orders/${item.id}`}
                  className="activity-row"
                >
                  <span
                    className={`activity-icon ${activityTone(item.status)}`}
                  >
                    {activityIcon(item.status)}
                  </span>
                  <div style={{ flex: 1 }}>
                    <strong>{item.title}</strong>
                    <small>{item.meta}</small>
                  </div>
                  <time>{formatRelative(item.time)}</time>
                </Link>
              ))
            )}
          </div>
        </div>
      </section>
    </>
  );
};

const colorMap: Record<string, string> = {
  purple: "var(--purple)",
  blue: "var(--blue)",
  green: "var(--green)",
  orange: "var(--orange)",
  pink: "var(--pink)",
};

const buildConicGradient = (sources: any[] = []) => {
  const total = sources.reduce(
    (sum, source) => sum + Number(source.value || 0),
    0,
  );
  if (!total) {
    return "conic-gradient(#e7eaf0 0 100%)";
  }

  let cursor = 0;
  const stops = sources.map((source) => {
    const start = cursor;
    const size = (Number(source.value || 0) / total) * 100;
    cursor += size;
    return `${colorMap[source.color] || "var(--purple)"} ${start.toFixed(2)}% ${cursor.toFixed(2)}%`;
  });

  return `conic-gradient(${stops.join(", ")})`;
};

const activityTone = (status = "") => {
  const normalized = status.toLowerCase();
  if (normalized.includes("deliver")) return "green";
  if (normalized.includes("ship")) return "blue";
  if (normalized.includes("cancel") || normalized.includes("refund"))
    return "pink";
  return "purple";
};

const activityIcon = (status = "") => {
  const normalized = status.toLowerCase();
  if (normalized.includes("deliver")) return "✓";
  if (normalized.includes("ship")) return "🚚";
  if (normalized.includes("cancel") || normalized.includes("refund"))
    return "!";
  return "🛒";
};

const formatRelative = (date?: string) => {
  if (!date) return "-";
  const diff = Date.now() - new Date(date).getTime();
  const minutes = Math.max(Math.floor(diff / 60000), 0);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

export default Dashboard;
